import React, {Suspense, useEffect, useState} from 'react';
import {Navigate, Outlet, useLocation, useNavigate} from 'react-router-dom';
import {ModalProvider, useModal} from '@ui/Modal';
import {ButtonPrimary, ButtonTernary} from '@ui/Button';
import {useAppDispatch, useAppSelector} from '@hooks/app';
import {setToken} from '@store/app.slice';
import {hasVerifiedEmail, VERIFICATION_COMPLETED_EVENT, VERIFICATION_REQUIRED_EVENT} from '@utils/verification';
import {ProfileApi, useGetUserProfileQuery} from '../Profile/Profile.api';
import {CheckAuthDialog} from '../../components/CheckAuthDialog';
import {SiteVisitTracker} from '../../components/SiteVisitTracker';
import {UserNavigationTracker} from '../../components/UserNavigationTracker';
import {OnlinePingTracker} from '../../components/OnlinePingTracker';
import {RegistrationWelcome} from '../../components/FreeAccess/FreeAccessDialogs';
import {IProfile} from '../Profile/types';

const WelcomeListener = ({profile}: {profile?: IProfile}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const modal = useModal(RegistrationWelcome, {replaceContent: true});
    useEffect(() => {
        if (!(location.state as {registrationWelcome?: boolean})?.registrationWelcome || !hasVerifiedEmail(profile)) return;
        modal.open({profile, onClose: modal.close});
        navigate(location.pathname + location.search, {replace: true, state: null});
    }, [location.key, profile]);
    return <></>;
};

/** Проверяет существующую сессию до монтирования контента и фоновых трекеров. */
export const VerificationGate = () => {
    const token = useAppSelector(state => state.app.token);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [required, setRequired] = useState<{token: string; requestId?: string; at: number} | null>(null);
    const {currentData: profile, isFetching, error, refetch, requestId, fulfilledTimeStamp} = useGetUserProfileQuery({token}, {skip: !token?.token, refetchOnMountOrArgChange: true});
    const authPage = ['/login', '/registration', '/verify-email'].includes(location.pathname);
    useEffect(() => {
        const handle = (event: Event) => {
            if (!token?.token) return;
            const detail = (event as CustomEvent<{token?: string; deliveryMessage?: string}>).detail;
            if (detail?.token && detail.token !== token.token) return;
            setRequired({token: token.token, requestId, at: Date.now()});
            dispatch(ProfileApi.util.invalidateTags(['Profile']));
            if (detail) navigate('/verify-email', {replace: true, state: {deliveryMessage: detail.deliveryMessage}});
        };
        const complete = (event: Event) => {
            if ((event as CustomEvent<{token?: string}>).detail?.token === token?.token) setRequired(null);
        };
        window.addEventListener(VERIFICATION_REQUIRED_EVENT, handle);
        window.addEventListener(VERIFICATION_COMPLETED_EVENT, complete);
        return () => {
            window.removeEventListener(VERIFICATION_REQUIRED_EVENT, handle);
            window.removeEventListener(VERIFICATION_COMPLETED_EVENT, complete);
        };
    }, [dispatch, navigate, token?.token, requestId]);
    useEffect(() => {
        if (required?.token === token?.token && hasVerifiedEmail(profile) && !isFetching && !error
            && requestId !== required.requestId && (fulfilledTimeStamp ?? 0) >= required.at) setRequired(null);
    }, [profile, isFetching, error, requestId, fulfilledTimeStamp, required, token?.token]);
    const clearSession = () => {localStorage.removeItem('token'); dispatch(setToken({token: null})); dispatch(ProfileApi.util.resetApiState());};
    const unauthorized = (error as {status?: number})?.status === 401;
    useEffect(() => {if (unauthorized) clearSession();}, [unauthorized]);
    if (token?.token && !profile && !authPage) {
        return <div role="status" className="p-24">{error ? <><p>Не удалось проверить сессию.</p><ButtonPrimary onClick={refetch}>Повторить</ButtonPrimary><ButtonTernary onClick={clearSession}>Войти заново</ButtonTernary></> : 'Загрузка…'}</div>;
    }
    const pending = Boolean(token?.token && ((required !== null && required.token === token?.token) || !hasVerifiedEmail(profile)));
    if (pending && !authPage) return <Navigate to="/verify-email" replace/>;
    const invite = new URLSearchParams(location.search).get('invite_code');
    if (token?.token && hasVerifiedEmail(profile) && invite && location.pathname === '/login') return <Navigate to={`/verify-email?invite_code=${encodeURIComponent(invite)}`} replace/>;
    // Смена ключа уничтожает старые модалки, чтобы запрет не оставлял открытые данные.
    const verificationScreen = pending || location.pathname === '/verify-email';
    return <ModalProvider key={verificationScreen ? 'verification' : 'content'}>
        <CheckAuthDialog/>
        {!verificationScreen && <><SiteVisitTracker/><UserNavigationTracker/><OnlinePingTracker/><WelcomeListener profile={profile}/></>}
        <Suspense fallback={null}><Outlet/></Suspense>
    </ModalProvider>;
};
